<template>
  <div @click="selectedItem = null">
    <breadcrumbs home="Items: Reconcile inventory"/>

    <v-card flat>
      <v-form v-model="valid" ref="form">
        <v-container class="pt-4 px-6 pb-6" fluid>
          <v-row align="center" dense>
            <v-col cols="12" sm="4" md="3">
              <date-picker v-model="transaction.date" @change="refresh(items)" hide-details :disabled="readOnly"/>
            </v-col>
            <v-col cols="12" sm="4" v-if="sites.length>1">
              <v-autocomplete label="Site" dense outlined :items="sites" v-model="transaction.siteId" item-text="name" item-value="id" hide-details @change="refresh(items)" :disabled="readOnly"/>
            </v-col>
            <v-col cols="12" sm="4" md="3">
              <inventory-categories-select v-model="transaction.categoryId" label="Category" none-text="All" type="Item" hide-details/>
            </v-col>
            <v-col cols="3" sm="2">
              <v-switch label="Active only" dense hide-details class="ml-4 mt-0" v-model="transaction.activeOnly" :disabled="readOnly"/>
            </v-col>
          </v-row>
          <v-row dense class="mt-3">
            <v-col cols="6" sm="3">
              <v-select v-model="transaction.value" label="Adjust values" outlined dense hide-details :items="['None','Automatically','As specified']"
                        append-outer-icon="mdi-help-circle" :disabled="readOnly" @change="changeValue"
                        @click:append-outer="$store.dispatch('help', {title:'Inventory reconciliation',topicId:'RgADAAAA',articleId:'RwAlAAAA',ref:'reconcileValues'})"/>
            </v-col>
            <v-col cols="12" sm="6" md="4" v-if="company.connectedToAccounting && values && transaction.value !== 'None'">
              <account-picker v-model="transaction.expenseAccountRef" label="Inventory adjustment account"
                              :accounts="accounts" :disabled="readOnly"/>
            </v-col>
            <v-col cols="12" sm="6" md="4" v-if="negativeInventory">
              <v-checkbox v-model="transaction.ignoreNegativeInventory" label="Ignore items with negative inventory" class="mt-1 ml-4" hide-details/>
            </v-col>
          </v-row>
          <v-row v-if="$vuetify.breakpoint.mobile">
            <v-col>
              <scan-code @code="scan" :items="items"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pb-0">
              <outlined-block>
                <v-data-table width="100%" :height="$vuetify.breakpoint.mobile ? null : 'calc(100vh - 362px)'"
                              calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer fixed-header
                              :items="itemsList" item-key="key" no-data-text="None found" group-by="name"
                              class="no-sort"
                              :headers="[{text:'Item',value:'name',width:'35%',divider:true,class:'px-2',cellClass:'py-2 px-2'},
                                        {text:'Item / Lot',value:'lot',width:'20%',divider:true,class:'px-2',cellClass:'py-2 px-2'},
                                        {text:'Book inventory',value:'book',width:'15%',align:'right',divider:true,class:'px-2',cellClass:'py-2 px-2'},
                                        {text:'Physical inventory',value:'physical',width:'15%',divider:true,class:'px-2',cellClass:'py-2 px-2',align:'right'},
                                        {text:'Adjust by',value:'diff',width:'15%',align:'right',divider:true,class:'px-2',cellClass:'py-2 px-2'}]"
                              :item-class="item => item.key=== selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="item => {selectedItem = item.key; $nextTick(() => this.$refs.form.validate())}">
                  <template v-slot:group.header="{headers,group,items}">
                    <td :colspan="headers.length">
                      <strong>{{group}}</strong>
                      <v-btn v-if="items[0].lotTracking" x-small text color="primary" @click="addLot(items[0])" :disabled="readOnly">Add lot</v-btn>
                    </td>
                  </template>

                  <template v-slot:header.name>
                    <span class="mr-4">Item</span>
                    <scan-code @code="scan" :items="items"/>
                  </template>
                  <template v-slot:header.lot>
                    <span class="mr-4">Item / Lot</span>
                    <scan-code @code="scan" :items="items"/>
                  </template>

                  <template v-slot:header.physical>
                    Physical inventory
                    <v-tooltip bottom>
                      <template v-slot:activator="{on}">
                        <v-icon small v-on="on" @click="resetQuantities(false)">mdi-tag-off-outline</v-icon>
                      </template>
                      Set all physical quantities to zero
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{on}">
                        <v-icon small v-on="on" @click="resetQuantities(true)">mdi-refresh</v-icon>
                      </template>
                      Reset physical quantities to book quantities
                    </v-tooltip>
                  </template>
                  <template v-slot:item.lot="{item}">
                    <template v-if="item.editLot && selectedItem === item.key">
                      <v-text-field v-model="item.lot" class="d-inline-block" dense :rules="[fieldRules.required]"
                                    style="width:150px;left:50px" :disabled="readOnly" outlined/>
                      <v-icon small @click="deleteLot(item.key)" style="padding-left:50px">mdi-delete</v-icon>
                    </template>
                    <div v-else-if="item.lotTracking" :class="{'error--text':!item.lot}" style="padding-left:50px">{{item.lot || 'Required'}}</div>
                  </template>
                  <template v-slot:item.book="{item}">
                    <div :class="{'grey--text':item.bookQuantity<0&&transaction.ignoreNegativeInventory,'mt-n5':selectedItem === item.key}">
                      {{item.bookQuantity | number(item.precision)}} {{item.trackUnits}}
                    </div>
                    <div v-if="transaction.value==='As specified'" :class="{'grey--text':item.bookQuantity<0&&transaction.ignoreNegativeInventory,'mt-11':selectedItem === item.key}">
                      {{item.bookValue | currency}}
                    </div>
                  </template>
                  <template v-slot:item.physical="{item}">
                    <template v-if="selectedItem === item.key">
                      <decimal-number-input v-model="item.quantity" :outlined="false" prefix="" :precision="item.precision" :suffix="item.trackUnits"
                                            :disabled="item.bookQuantity<0&&transaction.ignoreNegativeInventory" no-minimum
                                            @change="updateQuantity(item)" outlined/>
                      <decimal-number-input v-if="transaction.value==='As specified'" v-model="item.value" no-minimum
                                            :disabled="item.bookQuantity<0&&transaction.ignoreNegativeInventory"/>
                    </template>
                    <template v-else>
                      <div :class="{'error--text':item.quantity < 0}">
                        {{item.quantity | number(item.precision)}} {{item.trackUnits}}
                      </div>
                      <div v-if="transaction.value==='As specified'" :class="{'error--text':item.value < 0}">{{item.value | currency}}</div>
                    </template>
                  </template>
                  <template v-slot:item.diff="{item}">
                    <div :class="{'mt-n5':selectedItem === item.key,'error white--text pa-1': item.quantity - item.bookQuantity && !(item.bookQuantity<0&&transaction.ignoreNegativeInventory)}">
                      {{item.quantity - item.bookQuantity | number(item.precision)}} {{item.trackUnits}}
                    </div>
                    <div v-if="transaction.value==='As specified'"
                         :class="{'mt-11':selectedItem === item.key,'error white--text pa-1': item.value - item.bookValue && !(item.bookQuantity<0&&transaction.ignoreNegativeInventory)}">
                      {{item.value - item.bookValue | currency}}
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pb-0">
              <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255)]" hide-details/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <v-spacer/>
        <v-btn :plain="!readOnly" color="primary" @click="cancel">{{readOnly ? 'Done' : 'Cancel'}}</v-btn>
        <v-btn color="primary" outlined :loading="saving" @click="save(true)" v-if="!readOnly">Save as draft</v-btn>
        <v-btn color="primary" :loading="saving" @click="save(false)" v-if="!readOnly">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import InventoryCategoriesSelect from '@/views/inventory/categories/InventoryCategoriesSelect'

export default {
  name: 'InventoryItemsReconcileEdit',
  mixins: [confirmLeave],
  components: {InventoryCategoriesSelect},
	props: {
    id: {type: String, required:false}
	},
	data() {
		return {
      valid: true,
      transaction: {items:[], value:'None', date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'), draft:false, categoryId:null,
                    ignoreNegativeInventory:true, activeOnly:true,
                    expenseAccountRef:'',comments:'Inventory reconciliation',siteId:this.$store.getters.sites(true, this.inBond ? true : undefined)[0].id},
      saving:false,
      accounts: [],
      items: [],
      selectedItem: null
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    itemsList() {
      return this.items.filter(item => (this.transaction.activeOnly ? item.active : true) &&
                                       (this.transaction.categoryId == null || this.transaction.categoryId === item.categoryId));
    },
    sites() {
      return this.$store.getters.sites(true, this.transaction.bonded ? true : undefined);
    },
    readOnly() {
      return !this.company.permissions.includes('inventory.write');
    },
    negativeInventory() {
      return this.items.some(item => item.bookQuantity < 0);
    },
    values() {
      return this.$store.getters.company.permissions.includes('values') && this.$store.getters.company.settings.useAccounts;
    }
  },
	async created() {
    const results = await this.$apiCall(`query InventoryItemsReconcileEdit($id:ID!, $edit:Boolean!, $accounting:Boolean!) {
                                            itemReconciliation(id:$id) @include(if:$edit) {
                                              id,value,date,comments,expenseAccountRef,ignoreNegativeInventory,site{id},draft,activeOnly,category{id}
                                              items{id,lot,quantity,value}
                                            }
                                            accounts @include(if:$accounting) {
                                              ref,parentRef,name,type,typeName
                                            }
                                            companyAccounts @skip(if:$edit) {inventoryAdjustment}
                                          }`,
                                        {id:this.id??'',edit:!!this.id,accounting:this.company.connectedToAccounting});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    if (this.company.connectedToAccounting)
      this.accounts = results.data.accounts;
    if (this.id) {
      const itemReconciliation = results.data.itemReconciliation;
      itemReconciliation.siteId = itemReconciliation.site.id;
      delete itemReconciliation.site;
      itemReconciliation.categoryId = itemReconciliation.category?.id ?? null;
      delete itemReconciliation.category;
      this.transaction = itemReconciliation;
    }
    else
      this.transaction.expenseAccountRef = results.data.companyAccounts.inventoryAdjustment;
    await this.refresh(this.transaction.items);
  },
	methods: {
    scan({type, id, quantity, cases, lot}) {
      if (type !== 'Item')
        return;
      lot = lot ?? '';
      let item = this.items.find(item => item.id === id && item.lot === lot);
      if (!item) {
        item = this.items.find(item => item.id === id);
        if (!item)
          return;
        item = this.addLot(item);
        item.lot = lot;
      }
      item.quantity += quantity;
    },

    resetQuantities(toBook) {
      for (const item of this.items) {
        item.quantity = toBook ? item.bookQuantity : 0;
        item.value = toBook ? item.bookValue : 0;
      }
    },
    changeValue() {
      for (const item of this.itemsList)
        this.updateQuantity(item);
    },
    updateQuantity(item) {
      if (this.transaction.value === 'As specified')
        item.value = item.bookQuantity === 0 ? 0 : this.$roundNumber(item.bookValue * (item.quantity / item.bookQuantity));
    },
    addLot(item) {
      let index = this.items.findIndex(i => i.id === item.id);
      while (this.items[index]?.id === item.id)
        index++;
      const lotItem = {id:item.id,name:item.name,trackUnits:item.trackUnits,precision:item.precision,lotTracking:true,
                       siteId:item.siteId,lot:'',active:item.active, categoryId:item.categoryId,
                       bookQuantity:0,quantity:0,bookValue:0,value:0,editLot:true,key:Date.now()}
      this.items.splice(index, 0, lotItem);
      this.selectedItem = lotItem.key
      this.$nextTick(() => this.$refs.form.validate());
      return lotItem;
    },
    deleteLot(key) {
      const index = this.items.findIndex(p => p.key === key);
      this.items.splice(index, 1);
    },
    async refresh(existingItems) {
      const items = [];
      await this.$lazyListLoad(items, 'items2', 'id,name,type,lotTracking,trackUnits,precision,active,category{id},sites {siteId,quantity,value,lot}', 100, [{field:'type',comparator:'eq',value:'Item'}], [{name:'asof', value:this.transaction.date}]);

      // Expand each item to include a row for each unique site/lot
      const newItems = [];
      let key = 1;
      for (const item of items) {
        if (item.type !== 'Item')
          continue;
        let foundItemAtSite = false;
        for (const site of item.sites) {
          if (site.siteId === this.transaction.siteId) {
            // Get current book values
            const bookQuantity = site.quantity;
            const bookValue = site.value;

            if (bookQuantity) {   // Add if there is inventory, otherwise ignore
              foundItemAtSite = true;
              newItems.push({id:item.id,name:item.name,lot:site.lot, active:item.active, categoryId:item.category?.id,
                              trackUnits:item.trackUnits,precision:item.precision,lotTracking:item.lotTracking,
                              bookQuantity, bookValue,
                              quantity: bookQuantity < 0 ? 0 : bookQuantity, value: bookValue,
                              key:key++});
            }
          }
        }
        if (!foundItemAtSite)    // No item at site, so add placeholder so item is at least in the list
          newItems.push({id:item.id,name:item.name,lot:'L0', active:item.active, categoryId:item.category?.id,
                          bookQuantity:0, bookValue:0,trackUnits:item.trackUnits,precision:item.precision,lotTracking:item.lotTracking,
                          quantity:0,value:0,
                          key:key++});
      }

      // Now, update item list from existing item list
      for (const existingItem of existingItems) {
        let item = newItems.find(p => p.id === existingItem.id && p.lot === existingItem.lot);
        const setFrom = {quantity:existingItem.quantity, value:existingItem.value};
        if (item)
          Object.assign(item, setFrom);
        else {
          item = items.find(p => p.id === existingItem.id);
          if (item)
            newItems.push({id:item.id,name:item.name,lot:'L0', bookQuantity:0, bookValue:0,trackUnits:item.trackUnits,precision:item.precision,lotTracking:item.lotTracking,
                            ...setFrom, key:key++});
        }
      }

      this.items = newItems;
    },
    cancel() {
      this.$router.back();
    },
		async save(draft) {
      if (!this.valid || this.itemsList.some(item => (item.lotTracking && !item.lot) || item.quantity < 0 || item.value < 0)) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.transaction.draft = draft;
      this.transaction.items = this.itemsList.map(item => ({id:item.id,lot:item.lot,quantity:item.quantity,
                                                        value:this.transaction.value==='None'?item.bookValue:(this.transaction.value==='Automatically'?null:item.value)}));
      this.saving = true;
      const results = await this.$apiCall(`mutation InventoryItemsReconcileEdit($input: ItemReconciliationInput!) {
                                              itemReconciliationCrupdate(input:$input) {id}
                                            }`,
                                          {input:this.transaction});
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`Created ${draft?'draft':''} inventory reconciliation`});
      }
		}
	}
}
</script>